import type { GameState, Player, LegalTarget } from './types';

// ─── Layout constants ───────────────────────────────────────────────────────
const SVG_W = 820;
const SVG_H = 480;
const MARGIN = 16;
const BAR_W = 28;
const BEAR_W = 52;

// Total usable width for the 24 triangles (minus bar and bear-off trays)
const BOARD_W = SVG_W - 2 * MARGIN - BAR_W - 2 * BEAR_W;
const TRI_W = BOARD_W / 12;        // width of each triangle slot
const TRI_H = SVG_H * 0.42;        // triangle height
const PIECE_R = TRI_W * 0.38;      // checker radius

// X origin of the left half of triangles (after left margin + bear tray)
const LEFT_X = MARGIN + BEAR_W;

// Top triangle tips point DOWN; bottom tips point UP.
// Point layout (viewing from white's side, white at bottom):
//   Top row L→R:  13 14 15 16 17 18  |bar|  19 20 21 22 23 24
//   Bot row L→R:  12 11 10  9  8  7  |bar|   6  5  4  3  2  1
const POINT_X: number[] = new Array(25).fill(0);
const POINT_Y_TOP: number[] = new Array(25).fill(0);  // top of slot
const POINTS_UP: boolean[] = new Array(25).fill(false);

(function buildLayout() {
  // Bottom row (tips UP, white starts here): L→R  13 14 15 16 17 18 | 19 20 21 22 23 24
  // Point 24 (white start) lands at bottom-right, closer to the human player.
  const botPoints = [13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24];
  for (let col = 0; col < 12; col++) {
    const pt = botPoints[col]!;
    const xBase = col < 6
      ? LEFT_X + col * TRI_W
      : LEFT_X + col * TRI_W + BAR_W;
    POINT_X[pt] = xBase + TRI_W / 2;
    POINT_Y_TOP[pt] = SVG_H - MARGIN - TRI_H;
    POINTS_UP[pt] = true;
  }
  // Top row (tips DOWN, white home here): L→R  12 11 10 9 8 7 | 6 5 4 3 2 1
  // Points 1–6 (white home / bear-off) are at top-right.
  const topPoints = [12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1];
  for (let col = 0; col < 12; col++) {
    const pt = topPoints[col]!;
    const xBase = col < 6
      ? LEFT_X + col * TRI_W
      : LEFT_X + col * TRI_W + BAR_W;
    POINT_X[pt] = xBase + TRI_W / 2;
    POINT_Y_TOP[pt] = MARGIN;
    POINTS_UP[pt] = false;
  }
})();

// ─── SVG helpers ────────────────────────────────────────────────────────────
function el<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number> = {}): SVGElementTagNameMap[K] {
  const e = document.createElementNS('http://www.w3.org/2000/svg', tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  return e;
}

function triPath(cx: number, tipY: number, pointsUp: boolean): string {
  const hw = TRI_W / 2;
  const baseY = pointsUp ? tipY + TRI_H : tipY;
  const tip_y  = pointsUp ? tipY        : tipY + TRI_H;
  return `M ${cx - hw} ${baseY} L ${cx + hw} ${baseY} L ${cx} ${tip_y} Z`;
}

// ─── Main render function ────────────────────────────────────────────────────
// Called on every state change. Completely redraws the SVG.
export function renderBoard(
  state: GameState,
  svg: SVGSVGElement,
  onPointClick: (point: number) => void,
  onBearOffClick: (player: Player) => void,
): void {
  while (svg.firstChild) svg.removeChild(svg.firstChild);

  drawBackground(svg);
  drawTriangles(svg, state);
  drawBar(svg);
  drawBearOffTrays(svg, state, onBearOffClick);
  drawPieces(svg, state, onPointClick);
  drawHighlights(svg, state, onPointClick, onBearOffClick);
  drawPointLabels(svg);
}

// ─── Colour palette ─────────────────────────────────────────────────────────
const C = {
  outerFrame:   '#0f0701',   // darkest bark / ebony outer frame
  boardSurface: '#3e1a07',   // dark walnut bark — the playing field
  barColor:     '#180c03',   // centre divider, darkest wood
  bearTray:     '#281205',   // bear-off tray
  triLight:     '#c08a38',   // light plank — warm heartwood (pine / maple)
  triDark:      '#7a4018',   // dark plank — deep grain (mahogany / cherry)
};

function addDefs(svg: SVGSVGElement) {
  const defs = el('defs', {});

  function grad(id: string, cx: string, cy: string, r: string, stops: Array<[string, string, string?]>) {
    const g = el('radialGradient', { id, cx, cy, r, gradientUnits: 'objectBoundingBox' });
    for (const [offset, color, opacity] of stops) {
      const s = el('stop', { offset, 'stop-color': color });
      if (opacity) s.setAttribute('stop-opacity', opacity);
      g.appendChild(s);
    }
    defs.appendChild(g);
  }

  // ── Convex dome gradients (highlight top-left) ──────────────────────────
  grad('gPW', '32%', '28%', '68%', [
    ['0%',   '#fff9ee', '1'],
    ['45%',  '#dbb86a', '1'],
    ['100%', '#7a4c18', '1'],
  ]);
  grad('gPB', '32%', '28%', '68%', [
    ['0%',   '#9090b0', '1'],
    ['40%',  '#282838', '1'],
    ['100%', '#050508', '1'],
  ]);

  // ── Concave divot gradients: light source opposite → bottom-right focus ─
  // Shadow ring (rim casts shadow inward; darkest near rim edges)
  grad('gDW', '62%', '68%', '62%', [
    ['0%',   '#6a3c12', '0.70'],  // floor corner (some ambient)
    ['55%',  '#2a1206', '0.90'],
    ['100%', '#0e0603', '1.00'],  // deep rim shadow
  ]);
  grad('gDB', '62%', '68%', '62%', [
    ['0%',   '#202030', '0.75'],
    ['55%',  '#0a0a14', '0.90'],
    ['100%', '#020204', '1.00'],
  ]);

  // Floor (deepest part of the pit — just a bit of ambient)
  grad('gFW', '58%', '65%', '65%', [
    ['0%',   '#7a4820', '0.60'],
    ['100%', '#1e0c04', '0.95'],
  ]);
  grad('gFB', '58%', '65%', '65%', [
    ['0%',   '#282838', '0.65'],
    ['100%', '#060610', '0.95'],
  ]);

  svg.appendChild(defs);
}

function drawBackground(svg: SVGSVGElement) {
  addDefs(svg);
  // Outer frame (ebony bark)
  svg.appendChild(el('rect', { x: 0, y: 0, width: SVG_W, height: SVG_H, fill: C.outerFrame, rx: 8 }));
  // Board playing surface (dark walnut bark)
  svg.appendChild(el('rect', {
    x: MARGIN, y: MARGIN,
    width: SVG_W - 2 * MARGIN, height: SVG_H - 2 * MARGIN,
    fill: C.boardSurface, rx: 4,
  }));
}

function drawTriangles(svg: SVGSVGElement, _state: GameState) {
  // Triangles alternate: pt 1,3,5… = dark plank; pt 2,4,6… = light plank
  for (let pt = 1; pt <= 24; pt++) {
    const cx = POINT_X[pt]!;
    const ty = POINT_Y_TOP[pt]!;
    const up = POINTS_UP[pt]!;
    const color = pt % 2 === 1 ? C.triDark : C.triLight;
    svg.appendChild(el('path', { d: triPath(cx, ty, up), fill: color }));
  }
}

function drawBar(svg: SVGSVGElement) {
  const barX = LEFT_X + TRI_W * 6;
  svg.appendChild(el('rect', { x: barX, y: MARGIN, width: BAR_W, height: SVG_H - 2 * MARGIN, fill: C.barColor }));
}

function drawBearOffTrays(
  svg: SVGSVGElement,
  state: GameState,
  onBearOffClick: (p: Player) => void,
) {
  const bearOffData: Array<{ player: Player; x: number }> = [
    { player: 'white', x: MARGIN },
    { player: 'black', x: SVG_W - MARGIN - BEAR_W },
  ];
  for (const { player, x } of bearOffData) {
    svg.appendChild(el('rect', { x, y: MARGIN, width: BEAR_W, height: SVG_H - 2 * MARGIN, fill: C.bearTray, rx: 3 }));
    const count = state.borneOff[player];
    for (let i = 0; i < count; i++) {
      const cy = SVG_H - MARGIN - 10 - i * 12;
      svg.appendChild(el('circle', {
        cx: x + BEAR_W / 2, cy, r: 7,
        fill: player === 'white' ? 'url(#gPW)' : 'url(#gPB)',
        stroke: player === 'white' ? '#a07830' : '#5050a0',
        'stroke-width': 1.5,
      }));
    }
    const label = el('text', { x: x + BEAR_W / 2, y: MARGIN + 14, 'text-anchor': 'middle', fill: '#c8a86888', 'font-size': '11', 'font-family': 'Georgia' });
    label.textContent = `${player[0]!.toUpperCase()}:${count}`;
    svg.appendChild(label);

    const clickArea = el('rect', { x, y: MARGIN, width: BEAR_W, height: SVG_H - 2 * MARGIN, fill: 'transparent', cursor: 'pointer' });
    clickArea.addEventListener('click', () => onBearOffClick(player));
    svg.appendChild(clickArea);
  }
}

// Compute Y positions for `totalCount` visual slots (pinned + top pieces combined).
// For ≤5 pieces: spaced so pieces don't overlap (each slot = one full diameter apart).
// For >5 pieces: compactly overlap, capping display at MAX_DISPLAY pieces.
const MAX_DISPLAY = 8;

function stackPositions(
  cx: number,
  tipY: number,
  pointsUp: boolean,
  totalCount: number,
): Array<{ x: number; y: number }> {
  if (totalCount <= 0) return [];

  const displayCount = totalCount <= 5 ? totalCount : Math.min(totalCount, MAX_DISPLAY);

  // Step between piece centres.
  // ≤5: spread evenly so pieces just touch (step = diameter = 2*PIECE_R).
  //  >5: compress to fit all displayCount pieces inside TRI_H.
  const step = totalCount <= 5
    ? 2 * PIECE_R                                           // touching, no overlap
    : (TRI_H - 2 * PIECE_R) / Math.max(displayCount - 1, 1); // fill the triangle

  const positions: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < displayCount; i++) {
    const offset = PIECE_R + step * i;
    const y = pointsUp ? tipY + TRI_H - offset : tipY + offset;
    positions.push({ x: cx, y });
  }
  return positions;
}

function drawPieces(svg: SVGSVGElement, state: GameState, onPointClick: (pt: number) => void) {
  for (let pt = 1; pt <= 24; pt++) {
    const ptState = state.points[pt]!;
    if (ptState.topOwner === null && ptState.pinnedOwner === null) continue;

    const cx = POINT_X[pt]!;
    const ty = POINT_Y_TOP[pt]!;
    const up = POINTS_UP[pt]!;

    const hasPinned = ptState.pinnedOwner !== null;
    const topCount  = ptState.topOwner !== null ? ptState.topCount : 0;
    const totalCount = (hasPinned ? 1 : 0) + topCount;

    const positions = stackPositions(cx, ty, up, totalCount);
    let slot = 0;

    // Slot 0: pinned piece (base of the triangle, same space as any other piece)
    if (hasPinned) {
      const pos = positions[slot]!;
      drawChecker(svg, pos.x, pos.y, ptState.pinnedOwner!, true, pt, onPointClick);
      slot++;
    }

    // Remaining slots: top-owner pieces
    if (ptState.topOwner !== null) {
      const slotsForTop = positions.length - slot;
      for (let i = 0; i < slotsForTop; i++) {
        const pos = positions[slot + i]!;
        drawChecker(svg, pos.x, pos.y, ptState.topOwner, false, pt, onPointClick);
      }
      // Count badge if some top pieces are off-screen
      if (topCount > slotsForTop) {
        const topPos = positions[positions.length - 1]!;
        const badge = el('text', {
          x: topPos.x, y: topPos.y + 4,
          'text-anchor': 'middle',
          fill: '#fff',
          'font-size': '10', 'font-weight': 'bold',
          'pointer-events': 'none',
        });
        badge.textContent = String(topCount);
        svg.appendChild(badge);
      }
    }
  }
}

function drawChecker(
  svg: SVGSVGElement,
  cx: number,
  cy: number,
  player: Player,
  isPinned: boolean,
  pt: number,
  onClick: (pt: number) => void,
) {
  const isWhite = player === 'white';
  const g = el('g', { cursor: 'pointer' });
  drawCheckerBody(g, cx, cy, isWhite);
  g.addEventListener('click', (e) => { e.stopPropagation(); onClick(pt); });
  svg.appendChild(g);
}

// Shared piece body — used for both real pieces and ghost pieces.
function drawCheckerBody(
  parent: SVGElement,
  cx: number,
  cy: number,
  isWhite: boolean,
): void {
  parent.appendChild(el('circle', {
    cx, cy, r: PIECE_R,
    fill:           isWhite ? 'url(#gPW)' : 'url(#gPB)',
    stroke:         isWhite ? '#9a7030'   : '#4a4a90',
    'stroke-width': 2,
  }));

  const divotR = PIECE_R * 0.47;
  parent.appendChild(el('circle', { cx, cy, r: divotR,        fill: isWhite ? 'url(#gDW)' : 'url(#gDB)' }));
  parent.appendChild(el('circle', { cx, cy, r: PIECE_R * 0.28, fill: isWhite ? 'url(#gFW)' : 'url(#gFB)' }));
  parent.appendChild(el('circle', {
    cx: cx - divotR * 0.38,
    cy: cy - divotR * 0.38,
    r:  divotR * 0.20,
    fill: isWhite ? 'rgba(255,245,220,0.55)' : 'rgba(160,160,220,0.35)',
    'pointer-events': 'none',
  }));
}

// Returns the SVG {x,y} of the piece at `slotIndex` for a point.
function slotPos(pt: number, slotIndex: number, totalSlots: number): { x: number; y: number } {
  const cx = POINT_X[pt]!;
  const ty = POINT_Y_TOP[pt]!;
  const up = POINTS_UP[pt]!;
  const positions = stackPositions(cx, ty, up, Math.max(totalSlots, 1));
  return positions[Math.min(slotIndex, positions.length - 1)]!;
}

// Total occupied slots at a point (pinned counts as 1, plus topCount).
function totalSlots(state: GameState, pt: number): number {
  const p = state.points[pt]!;
  return (p.pinnedOwner !== null ? 1 : 0) + (p.topOwner !== null ? p.topCount : 0);
}

function drawHighlights(
  svg: SVGSVGElement,
  state: GameState,
  onPointClick: (pt: number) => void,
  onBearOffClick: (player: Player) => void,
): void {
  // ── Yellow selection ring on the actual piece being moved ────────────────
  // Normal mode: ring sits on top of the current stack (last slot).
  // Reine mode: piece was lifted — ring sits one above the remaining stack.
  const srcPt = state.selection ?? state.reinePickup?.point ?? null;
  if (srcPt !== null) {
    const current = totalSlots(state, srcPt);
    const ringSlot  = state.reinePickup !== null ? current      : current - 1;
    const ringTotal = state.reinePickup !== null ? current + 1  : current;
    const pos = slotPos(srcPt, ringSlot, ringTotal);
    svg.appendChild(el('circle', {
      cx: pos.x, cy: pos.y, r: PIECE_R + 4,
      fill: 'none', stroke: '#ffdd00', 'stroke-width': 3, opacity: '0.9',
      'pointer-events': 'none',
    }));
  }

  if (state.legalTargets.length === 0) return;

  // Owner of the ghost piece (may differ from currentPlayer in reine mode)
  const ghostOwner = state.reinePickup?.owner ?? state.currentPlayer;
  const isWhite    = ghostOwner === 'white';

  for (const { to } of state.legalTargets) {
    if (to === 0) {
      const tx = ghostOwner === 'white' ? MARGIN : SVG_W - MARGIN - BEAR_W;
      const tCx = tx + BEAR_W / 2;
      const nextCy = SVG_H - MARGIN - 10 - state.borneOff[ghostOwner] * 12;

      // Glowing border around the tray
      svg.appendChild(el('rect', {
        x: tx - 2, y: MARGIN - 2,
        width: BEAR_W + 4, height: SVG_H - 2 * MARGIN + 4,
        fill: 'none',
        stroke: isWhite ? '#ffdd00' : '#c060ff',
        'stroke-width': 2.5,
        rx: 4,
        'pointer-events': 'none',
      }));

      // Ghost scored checker at the next available slot
      svg.appendChild(el('circle', {
        cx: tCx, cy: nextCy, r: 7,
        fill: isWhite ? 'url(#gPW)' : 'url(#gPB)',
        stroke: isWhite ? '#ffdd00' : '#c060ff',
        'stroke-width': 1.5,
        opacity: '0.5',
        'pointer-events': 'none',
      }));

      // Full-tray click target on top
      const hit = el('rect', {
        x: tx, y: MARGIN, width: BEAR_W, height: SVG_H - 2 * MARGIN,
        fill: 'transparent', cursor: 'pointer',
      });
      hit.addEventListener('click', () => onBearOffClick(ghostOwner));
      svg.appendChild(hit);
      continue;
    }

    // Ghost piece rendered at exactly the slot it would occupy after the move
    const before = totalSlots(state, to);
    const ghostPos = slotPos(to, before, before + 1);

    const g = el('g', { cursor: 'pointer', opacity: '0.38' });
    drawCheckerBody(g, ghostPos.x, ghostPos.y, isWhite);
    // Invisible hit-area slightly larger than the piece for easy clicking
    const hit = el('circle', { cx: ghostPos.x, cy: ghostPos.y, r: PIECE_R + 4, fill: 'transparent' });
    hit.addEventListener('click', (e) => { e.stopPropagation(); onPointClick(to); });
    g.appendChild(hit);
    g.addEventListener('click', (e) => { e.stopPropagation(); onPointClick(to); });
    svg.appendChild(g);
  }
}

function drawPointLabels(svg: SVGSVGElement) {
  for (let pt = 1; pt <= 24; pt++) {
    const cx = POINT_X[pt]!;
    const up = POINTS_UP[pt]!;
    const y  = up ? SVG_H - MARGIN + 12 : MARGIN - 4;
    const label = el('text', { x: cx, y, 'text-anchor': 'middle', fill: '#f5e6c888', 'font-size': '10', 'font-family': 'Georgia', 'pointer-events': 'none' });
    label.textContent = String(pt);
    svg.appendChild(label);
  }
}
